/**
 * Join insurer logo path with configured base without producing `//api/logos/...`
 * (trailing slash on VUE_APP_LOGO_URL + leading slash on path = separate cache key).
 */
export function buildInsurerLogoUrl(logoPath: string): string {
  if (!logoPath) return "";
  if (/^https?:\/\//i.test(logoPath) || logoPath.startsWith("data:")) {
    return logoPath;
  }

  const path = logoPath.startsWith("/") ? logoPath : `/${logoPath}`;
  const logoBase = process.env.VUE_APP_LOGO_URL?.replace(/\/$/, "");
  if (logoBase) {
    return `${logoBase}${path}`;
  }

  const isLocalEnv = process.env.VUE_APP_TARIFF_MANAGER_URL !== undefined;
  const basePath = isLocalEnv
    ? process.env.VUE_APP_TARIFF_MANAGER_URL?.replace(/\/$/, "") || ""
    : "";
  return `${basePath}${path}`;
}

/** Enough logos for the magnifying-glass strip without saturating the API host. */
export const SPINNER_LOGO_PREFETCH_LIMIT = 10;

export type PrefetchInsurerLogosOptions = {
  limit?: number;
  maxConcurrent?: number;
  /** Called once per URL when that image decoded successfully (incl. cache hit). */
  onLoaded?: (url: string) => void;
};

function resolveLogoUrls(
  logoPaths: Array<{ logo?: string } | string>,
  limit: number,
): string[] {
  return [
    ...new Set(
      logoPaths
        .map((entry) =>
          typeof entry === "string"
            ? buildInsurerLogoUrl(entry)
            : buildInsurerLogoUrl(entry?.logo ?? ""),
        )
        .filter(Boolean),
    ),
  ].slice(0, limit);
}

/**
 * Warm the browser cache for spinner logos. Same host as the tariffs XHR
 * (`localhost:8080` locally) — keep concurrency low so we do not starve search.
 * Invokes `onLoaded` per URL so the UI can start animating before all finish.
 */
export function prefetchInsurerLogos(
  logoPaths: Array<{ logo?: string } | string>,
  limitOrOptions:
    number | PrefetchInsurerLogosOptions = SPINNER_LOGO_PREFETCH_LIMIT,
): void {
  if (typeof window === "undefined" || typeof Image === "undefined") return;

  const options: PrefetchInsurerLogosOptions =
    typeof limitOrOptions === "number"
      ? { limit: limitOrOptions }
      : limitOrOptions;

  const limit = options.limit ?? SPINNER_LOGO_PREFETCH_LIMIT;
  const maxConcurrent = options.maxConcurrent ?? 3;
  const onLoaded = options.onLoaded;
  const urls = resolveLogoUrls(logoPaths, limit);

  let inFlight = 0;
  let cursor = 0;

  const startNext = () => {
    while (inFlight < maxConcurrent && cursor < urls.length) {
      const url = urls[cursor++];
      inFlight += 1;
      let settled = false;
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        inFlight -= 1;
        if (ok) onLoaded?.(url);
        startNext();
      };
      const img = new Image();
      img.onload = () => finish(true);
      img.onerror = () => finish(false);
      img.src = url;
      if (img.complete) {
        finish(img.naturalWidth > 0);
      }
    }
  };

  startNext();
}
